import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { SpassuCodeApi } from '../../api';
import type { UseProjectRoleResult } from '../../hooks/useProjectRole';
import { useAzureRepositoryOptions } from './useAzureRepositoryOptions';

export interface CreationRepositoryValue {
  project: string;
  repository: string;
  branch: string;
}

export function useCreationRepository(
  api: SpassuCodeApi,
  enabled: boolean,
  permissions: UseProjectRoleResult,
) {
  const [value, setValue] = useState<CreationRepositoryValue>({
    project: '',
    repository: '',
    branch: '',
  });
  const {
    isAdmin,
    loading: rbacLoading,
    error: rbacError,
    hasPermission,
  } = permissions;
  const options = useAzureRepositoryOptions(api, {
    enabled,
    project: value.project,
    repository: value.repository,
    isAdmin,
    rbacLoading,
    rbacError,
    hasPermission,
  });
  useRepositoryDefaults(value, options, setValue);
  const reset = () => setValue({ project: '', repository: '', branch: '' });
  const selectProject = (project: string) =>
    setValue({ project, repository: '', branch: '' });
  const selectRepository = (repository: string) =>
    setValue(previous => ({ ...previous, repository, branch: '' }));
  const selectBranch = (branch: string) =>
    setValue(previous => ({ ...previous, branch }));
  const loading = Object.values(options).some(
    query => query.status === 'loading',
  );
  return {
    value,
    options,
    loading,
    reset,
    selectProject,
    selectRepository,
    selectBranch,
  };
}

function useRepositoryDefaults(
  value: CreationRepositoryValue,
  options: ReturnType<typeof useAzureRepositoryOptions>,
  setValue: Dispatch<SetStateAction<CreationRepositoryValue>>,
) {
  const repositories = options.repositories.items;
  const branches = options.branches.items;
  useEffect(() => {
    if (!repositories.some(repository => repository.name === value.repository))
      setValue(previous =>
        previous.repository ? { ...previous, repository: '' } : previous,
      );
  }, [repositories, value.repository, setValue]);
  useEffect(() => {
    setValue(previous => ({
      ...previous,
      branch: branches.find(branch => branch.isDefault)?.name ?? '',
    }));
  }, [branches, setValue]);
}
